<template>
  <div>
    <div class="back-link mb-3" @click="backTapped">
      <span class="material-icons">arrow_left</span>
      <span>Back</span>
    </div>

    <h2 class="auth-heading">Log in</h2>

    <div v-if="errorMessage" class="alert alert-danger" role="alert">
      {{ errorMessage }}
    </div>
    <div v-if="noticeMessage" class="alert alert-success" role="status">
      {{ noticeMessage }}
    </div>

    <form @submit.prevent="login">
      <div class="pro-field mb-3">
        <label class="pro-field__label" for="loginEmail">Email</label>
        <input
          id="loginEmail"
          v-model.trim="email"
          type="email"
          class="form-control form-control-lg pro-field__input"
          placeholder="example@email.com"
          autocomplete="email"
          required
        >
      </div>

      <div class="pro-field mb-4">
        <label class="pro-field__label" for="loginPassword">Password</label>
        <div class="password-input">
          <input
            id="loginPassword"
            v-model="password"
            :type="showPassword ? 'text' : 'password'"
            class="form-control form-control-lg pro-field__input"
            placeholder="Your password"
            autocomplete="current-password"
            required
          >
          <button
            type="button"
            class="password-toggle"
            :aria-label="showPassword ? 'Hide password' : 'Show password'"
            @click="showPassword = !showPassword"
          >
            <span class="material-icons">{{ showPassword ? 'visibility_off' : 'visibility' }}</span>
          </button>
        </div>
      </div>

      <button
        type="submit"
        class="btn pro-btn pro-btn--accent w-100"
        :disabled="isLoading || !email || !password"
      >
        <template v-if="isLoading">
          <span class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
          <span>Logging in…</span>
        </template>
        <template v-else>
          <span class="material-icons">arrow_forward</span>
          <span>Log in</span>
        </template>
      </button>

      <div class="pro-claim-card__links">
        <a href="#" @click.prevent="resetPassword">Forgot your password? Send reset email</a>
      </div>
    </form>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import { isValidEmail } from '../../../utils/helpers/IsValidEmail';

export default defineComponent({
  name: 'OnBoardingEmailLoginPage',
  data() {
    return {
      email: '',
      password: '',
      showPassword: false,
    };
  },
  computed: {
    isLoading(): boolean {
      return this.$store.state.onBoarding.isLoading;
    },
    errorMessage(): string | null {
      return this.$store.state.onBoarding.errorMessage;
    },
    noticeMessage(): string | null {
      return this.$store.state.onBoarding.noticeMessage;
    },
  },
  methods: {
    backTapped() {
      this.$store.commit('onBoarding/setError', null);
      this.$store.commit('onBoarding/setNotice', null);
      this.$store.commit('onBoarding/popPage');
    },
    async login() {
      // Clear any password-reset notice from a previous tap so it doesn't
      // sit alongside a fresh login attempt.
      this.$store.commit('onBoarding/setNotice', null);
      await this.$store.dispatch('onBoarding/loginWithEmail', {
        email: this.email,
        password: this.password,
      });
    },
    resetPassword() {
      if (this.email && isValidEmail(this.email)) {
        this.$store.dispatch('onBoarding/resetPassword', this.email);
      } else {
        this.$store.commit('onBoarding/setError', 'Enter a valid email above, then tap Reset password.');
      }
    },
  },
  beforeUnmount() {
    this.$store.commit('onBoarding/setError', null);
    this.$store.commit('onBoarding/setNotice', null);
  },
});
</script>

<style scoped>
.back-link {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  color: #a1a4ad;
  cursor: pointer;
  font-size: 0.85rem;
  font-weight: 600;
}
.back-link:hover {
  color: #fff;
}

.auth-heading {
  font-family: 'Montserrat', Helvetica, Arial, sans-serif;
  font-size: clamp(1.4rem, 2.4vw, 1.75rem);
  font-weight: 800;
  line-height: 1.15;
  letter-spacing: -0.01em;
  color: #fff;
  margin: 0 0 1.25rem;
}

.password-input {
  position: relative;
}
.password-toggle {
  position: absolute;
  right: 8px;
  top: 50%;
  transform: translateY(-50%);
  background: transparent;
  border: none;
  color: rgba(255, 255, 255, 0.55);
  cursor: pointer;
  padding: 6px;
  display: flex;
  align-items: center;
  border-radius: 8px;
}
.password-toggle:hover {
  color: #fff;
  background: rgba(255, 255, 255, 0.06);
}
.password-toggle .material-icons {
  font-size: 20px;
}
</style>
